import { BaseEntity } from '@/api/EntityApi';
import { DeepKeys } from '@tanstack/react-table';
import { Product } from '@/api/ProductApi';
import LANGUAGES from '@/configs/languages';

export type Language = keyof typeof LANGUAGES;
export type ForeignLanguage = Exclude<Language, 'PL'>;
export type FullTranslations<TData> = Record<Language, TData>;
export type Translations<TData> = Record<ForeignLanguage, TData>;

export type SortOrder = 'ASC' | 'DESC';

export interface Sort<TData extends BaseEntity = any> {
	key: DeepKeys<TData> | null;
	order: SortOrder;
}

export const formatSortKey = (key: string): string => {
	return key.replace(/-/g, '.');
};

export const formatSortParam = <TData extends BaseEntity>(
	sort: Sort<TData>
): string => {
	if (!sort.key) return '';
	return `${formatSortKey(sort.key.toString())}:${sort.order}`;
};

export interface ApiQueryOptions<TData extends BaseEntity> {
	page: number;
	limit: number;
	search: string;
	sortBy: Sort<TData>;
}

export type CustomFilters = Record<string, unknown>;

export interface ApiPaginatedResponse<TData extends {} = {}> {
	data: TData[];
	meta: {
		currentPage: number;
		itemsPerPage: number;
		totalItems: number;
		totalPages: number;
	};
}

export interface ApiFormattedResponseData<TData extends {} = {}> {
	data: TData[];
	totalPages: number;
}

export interface CreateManyResponse {
	passed: string[];
	rejected: string[];
}

export interface Attachment {
	id: UUID;
	path: string;
	name: string;
	size: number;
	mimetype: string;
}

export interface StringSignature<TData = any> {
	[key: string]: TData;
}

export type UUID = ReturnType<typeof crypto.randomUUID>;

export enum Province {
	DOLNOSLASKIE = 'Województwo dolnośląskie',
	KUJAWSKO_POMORSKIE = 'Województwo kujawsko-pomorskie',
	LUBELSKIE = 'Województwo lubelskie',
	LUBUSKIE = 'Województwo lubuskie',
	LODZKIE = 'Województwo łódzkie',
	MALOPOLSKIE = 'Województwo małopolskie',
	MAZOWIECKIE = 'Województwo mazowieckie',
	OPOLSKIE = 'Województwo opolskie',
	PODKARPACKIE = 'Województwo podkarpackie',
	PODLASKIE = 'Województwo podlaskie',
	POMORSKIE = 'Województwo pomorskie',
	SWIETOKRZYSKIE = 'Województwo świętokrzyskie',
	WARMINSKO_MAZURSKIE = 'Województwo warmińsko-mazurskie',
	WIELKOPOLSKIE = 'Województwo wielkopolskie',
	SLASKIE = 'Województwo śląskie',
	ZACHODNIO_POMORSKIE = 'Województwo zachodniopomorskie',
}

export enum ComponentRelationType {
	ATTRIBUTE_VALUE = 'attributeValue',
	PRODUCT_PROPERTY = 'productProperty',
	ATTRIBUTE = 'attribute',
}

export enum ComponentRelationAction {
	SHOW = 'show',
	HIDE = 'hide',
}

export enum RelationConditionOperator {
	LTE = 'lte',
	GTE = 'gte',
	EQ = 'eq',
}

export enum ComponentRelationOperator {
	OR = 'or',
	AND = 'and',
}

export interface ComponentRelation {
	action: ComponentRelationAction;
	operator: ComponentRelationOperator;
	conditions: ComponentRelationCondition[];
}

export interface ComponentRelationCondition {
	type: ComponentRelationType;
	objectId?: UUID;
	property?: keyof Pick<Product, 'surface' | 'weight'>;
	propertyValue?: number;
	propertyOperator?: RelationConditionOperator;
}

export interface PriceRule {
	amount: number;
	type: 'percentage' | 'fixed';
}

export enum Dimension {
	WIDTH = 'width',
	HEIGHT = 'height',
}
